// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import {
  checkInQuestionConfig,
  CheckInQuestionId,
  CheckInQuestionInfo,
  CheckInSectionId,
  CheckInStatus,
  checkInToolSpecs,
  isNonemptyStringArray,
  UsNeCheckInResponse,
} from "~@jii/configs";

/**
 * Return all the questions that should be shown to the resident, including
 * information about the question's section, based on the resident's answers.
 */
export function getVisibleQuestions({
  version,
  answers,
}: UsNeCheckInResponse): CheckInQuestionInfo[] {
  return checkInToolSpecs[version].flatMap(({ id: sectionId, questions }) => {
    return questions.flatMap((questionId) => {
      const questionInfo = [{ questionId, sectionId }];

      const { showIf } = checkInQuestionConfig[questionId];

      // Show questions that don't depend on anything
      if (!showIf) {
        return questionInfo;
      }

      // Show questions whose show-if condition was met
      const dependencyAnswer = answers[showIf.questionId];
      const dependencyFormat = checkInQuestionConfig[showIf.questionId].format;

      if (dependencyAnswer === showIf.value) {
        return questionInfo;
      }

      if (
        // these first two conditions should be identical, the second one
        // just narrows the type of dependencyAnswer
        dependencyFormat === "CHECKBOX" &&
        isNonemptyStringArray(dependencyAnswer) &&
        dependencyAnswer.includes(showIf.value)
      ) {
        return questionInfo;
      }

      return [];
    });
  });
}

/**
 * Return the ordinal position of the given questionId among the questions in its section.
 */
export function positionOfQuestion(
  questionId: CheckInQuestionId,
  sectionId: CheckInSectionId,
  response: UsNeCheckInResponse,
): number {
  const questionsInSection = getVisibleQuestions(response).filter(
    (q) => q.sectionId === sectionId,
  );
  return questionsInSection.findIndex((q) => q.questionId === questionId) + 1;
}

/**
 * Return the status and location of a resident within the Check-In Tool based on
 * the given answers.
 */
export function checkInStatus(response: UsNeCheckInResponse): CheckInStatus {
  const { answers } = response;
  const visibleQuestions = getVisibleQuestions(response);

  const firstUnansweredIndex = answers
    ? visibleQuestions.findIndex(
        ({ questionId }) => answers[questionId] === undefined,
      )
    : 0;

  // The resident hasn't answered the first question
  if (firstUnansweredIndex === 0) {
    return { kind: "NOT_STARTED", firstQuestion: visibleQuestions[0] };
  }

  // The resident has answered all visible questions
  if (firstUnansweredIndex === -1) {
    return { kind: "COMPLETED" };
  }

  // The resident has answered at least one question, so show the first unanswered question
  const currentQuestion = visibleQuestions[firstUnansweredIndex];

  const isFirstQuestionOfSection =
    visibleQuestions[firstUnansweredIndex - 1].sectionId !==
    currentQuestion.sectionId;

  return {
    kind: "QUESTION",
    question: currentQuestion,
    isFirstQuestionOfSection,
  };
}
